import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import GeneralLayout from '../../../components/GeneralLayout';
import ContentBox from '../../../components/ContentBox';
import { checkUserRoles } from '../../../services/api';

// DEBUG: set to 'STF' or 'OCC' to simulate that role regardless of actual admin status
const DEBUG_ROLE_OVERRIDE = null;

function AwardStamps({ userData }) {
    const [habboName, setHabboName] = useState('');
    const [amount, setAmount] = useState(1);
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [myRoles, setMyRoles] = useState(null);

    const isAdmin = DEBUG_ROLE_OVERRIDE ? false : userData?.hasAdminRole === true;

    useEffect(() => {
        if (!userData?.habboName) return;
        checkUserRoles(userData.habboName)
            .then(data => {
                if (data?.success) setMyRoles({ isSTF: data.isStaff, isOCC: data.isOCC });
            })
            .catch(() => {});
    }, [userData?.habboName]);

    if (!userData || !userData.hasStaffRole) {
        return <Navigate to="/" replace />;
    }

    const isOCC = !isAdmin && (DEBUG_ROLE_OVERRIDE ? DEBUG_ROLE_OVERRIDE === 'OCC' : myRoles?.isOCC === true);
    const isSTF = !isAdmin && (DEBUG_ROLE_OVERRIDE ? DEBUG_ROLE_OVERRIDE === 'STF' : myRoles?.isSTF === true);
    const maxAmount = isAdmin ? null : isOCC ? 500 : null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!habboName.trim()) {
            toast.error('Habbo name is required');
            return;
        }

        if (!isAdmin && habboName.trim().toLowerCase() === (userData.habboName || '').toLowerCase()) {
            toast.error('You cannot award framps to yourself');
            return;
        }

        const parsedAmount = parseInt(amount, 10);
        if (!parsedAmount || parsedAmount < 1) {
            toast.error('Amount must be at least 1');
            return;
        }

        if (!isAdmin && maxAmount !== null && parsedAmount > maxAmount) {
            toast.error(`You can award a maximum of ${maxAmount} framps at once`);
            return;
        }

        if (!reason.trim()) {
            toast.error('Reason is required');
            return;
        }

        try {
            setSubmitting(true);
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/';
            const response = await fetch(`${apiUrl}stamps/admin/award`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    habboName: habboName.trim(),
                    amount: parsedAmount,
                    reason: reason.trim(),
                    ...(DEBUG_ROLE_OVERRIDE ? { debugRoleOverride: DEBUG_ROLE_OVERRIDE } : {})
                })
            });

            const data = await response.json();

            if (data.success) {
                toast.success(`${parsedAmount} framp${parsedAmount !== 1 ? 's' : ''} awarded to ${habboName}`);
                setHabboName('');
                setAmount(1);
                setReason('');
            } else {
                toast.error(data.message || 'Failed to award framps');
            }
        } catch (error) {
            console.error('Error awarding stamps:', error);
            toast.error('Failed to award framps');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <GeneralLayout userData={userData}>
            <ContentBox theme="default" tint="lightskyblue" header="Award Framps">
                <div className="p-2 border border-black/30 mb-4 text-sm space-y-1">
                    <div className={isSTF ? 'bg-yellow-100 -mx-2 px-2 py-0.5 rounded' : undefined}><strong>Frankly Staff:</strong> May freely award framps to non-staff members. When awarding another staff member: limited to 100 framps per award, a 1-hour cooldown before awarding that staff member again, and that staff member may only receive a maximum of 5 awards per day and 10 awards per week from you. Cannot award yourself.</div>
                    <div className={isOCC ? 'bg-yellow-100 -mx-2 px-2 py-0.5 rounded' : undefined}><strong>Content Creators:</strong> May award up to 500 framps to any user (except yourself) once per day per person. The same user cannot be awarded again until the following day.</div>
                    <div><strong>Admins:</strong> Admins have no restrictions. If you want to award more then your restricted amount, please ask an Admin.</div>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-700 mb-2">
                            Habbo Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={habboName}
                            onChange={(e) => setHabboName(e.target.value)}
                            placeholder="Enter Habbo username"
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm"
                            disabled={submitting}
                        />
                    </div>

                    <div>
                        <label className="block font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-700 mb-2">
                            Amount <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            min="1"
                            max={maxAmount ?? undefined}
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm"
                            disabled={submitting}
                        />
                        {!isAdmin && maxAmount !== null && (
                            <p className="mt-1 text-[11px] text-gray-400">Max {maxAmount} per award</p>
                        )}
                    </div>

                    <div>
                        <label className="block font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-700 mb-2">
                            Reason <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Competition winner, Staff bonus..."
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm"
                            disabled={submitting}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={submitting || !habboName.trim() || !reason.trim() || amount < 1}
                        className="w-full font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-4 py-2 bg-maroon text-white hover:bg-red-900 transition-colors rounded disabled:opacity-50"
                        style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                    >
                        {submitting ? 'Awarding...' : 'Award Framps'}
                    </button>
                </form>
            </ContentBox>
        </GeneralLayout>
    );
}

export default AwardStamps;
